import { PlatformLocation } from '@angular/common';
import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';

import { StringConstants } from 'app/rcr-exchange/constants/string-constants';
import { isNullOrUndefined } from 'is-what';
import { FmLoadingPageService } from '../../shared/services/fm-loading-page.service';
import { UserService } from '../../shared/services/user.service';
import type { CalculateFeeMegaResponse } from '../services/interfaces/calculate-fee-mega-response';
import { MegasService } from '../services/megas.service';
import { PassDataService } from '../services/pass-data.service';
import { FeeDisclaimerService } from '../../shared/services/fee-disclaimer.service';

@Component({
  standalone: false,
  selector: 'invd-collateral-analyzer-review-and-commit',
  templateUrl: './collateral-analyzer-review-and-commit.component.html',
  styleUrls: ['./collateral-analyzer-review-and-commit.component.scss'],
})
export class CollateralAnalyzerReviewAndCommitComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  public calcFeeRes: CalculateFeeMegaResponse;
  public megaPricingUpdateList: any;
  public feeTicks: string;
  public fee: string;
  public megaType = '';
  public destinationUrl = '/';
  public isAllowToNavigateAway = false;
  public isEditMegaFlow = false;
  public poolNumber: number;
  public cusip = '';
  public revisionNumber: number;
  public showEarlyCloseLegend = false;
  public isCommitButtonClicked = false;
  showFeature = false;
  displayCancelCollateralPopup = false;
  cancelButtonLabel = '';
  cancelUploadCollateralInfoText = '';
  uploadNewCollateralLinkClicked = false;
  cancelAndStartOverButtonClicked = false;
  cancelPopUpTitle = '';
  public showCommitErrorMessage = false;
  public errorMsg = '';
  public disclaimerMessage = '';
  private feeTickValue = 0;

  private location = inject(PlatformLocation);
  private router = inject(Router);
  private passDataService = inject(PassDataService);
  private megaService = inject(MegasService);
  private userService = inject(UserService);
  private feeDisclaimerService = inject(FeeDisclaimerService);
  public fmLoadingPageService = inject(FmLoadingPageService);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.calcFeeRes = this.passDataService && this.passDataService.getCalculateFeeMegaResponse();
    if (this.calcFeeRes.freFeeTicksIndicator > 0) {
      this.feeTickValue = this.calcFeeRes.freFeeTicksIndicator;
      this.passDataService.setFeeTickValue(this.feeTickValue);
    }
    this.showEarlyCloseLegend =
      this.calcFeeRes && this.calcFeeRes.infoMessage !== null ? true : false;
    this.megaPricingUpdateList = this.calcFeeRes && this.calcFeeRes.megaPricingInfo[0].megaList;
    if (!isNullOrUndefined(this.calcFeeRes)) {
      this.megaType = this.calcFeeRes.megaType;
    }

    if (!isNullOrUndefined(this.calcFeeRes) && this.calcFeeRes.feeTicks !== null) {
      this.feeTicks = this.calcFeeRes.feeTicks.toFixed(3) + ' / 32';
    } else {
      this.fee = !isNullOrUndefined(this.calcFeeRes) ? this.calcFeeRes.feeDollars.toString() : null;
    }
  }

  ngAfterViewInit() {
    this.navigationController();
  }

  ngOnDestroy() {
    this.displayCancelCollateralPopup = false;
    this.cancelPopUpTitle = '';
    this.cancelButtonLabel = '';
    this.cancelUploadCollateralInfoText = '';
    this.uploadNewCollateralLinkClicked = false;
    this.cancelAndStartOverButtonClicked = false;
  }

  public goBack(): void {
    this.passDataService.setGoBackEditFlag('true');
    this.location.back();
  }

  canDeactivate(): Promise<boolean> | boolean {
    return this.isAllowToNavigateAway;
  }

  private navigationController() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.url.indexOf('file-upload') > 0) {
          this.isAllowToNavigateAway = true;
        } else if (event.url.indexOf('error') > 0) {
          this.passDataService.setMegaRepopulateBundle(null);
          this.passDataService.setEditMegaFlow(false);
          this.isAllowToNavigateAway = true;
        } else {
          this.isAllowToNavigateAway = false;
          this.destinationUrl = event.url;
        }
      }
      if (!(event instanceof NavigationEnd)) {
        return;
      }
      window.scrollTo(0, 0);
    });
  }

  public navigatePageTo() {
    this.router.navigate(['/collateral-analyzer/file-upload']);
    this.passDataService.setAnalyzerValidatedCollateralResponse(null);
    this.passDataService.setAnalyzerUploadedFileName(null);
    this.passDataService.setAnalyzerUploadedFileSheetName(null);
    this.passDataService.setGoBackEditFlag(false);
    this.isAllowToNavigateAway = true;
  }

  onReplaceNewCollateralFile() {
    this.displayCancelCollateralPopup = false;
    this.navigatePageTo();
  }

  public onUploadNewCollateral(): void {
    this.displayCancelCollateralPopup = true;
    this.cancelPopUpTitle = 'Upload New Collateral File';
    this.uploadNewCollateralLinkClicked = true;
    this.cancelButtonLabel = 'Yes, Replace';
    this.cancelUploadCollateralInfoText =
      'By replacing the uploaded collateral file, all data currently displayed will be removed.';
  }

  public onYesAndStartOver() {
    this.displayCancelCollateralPopup = true;
    this.navigatePageTo();
  }

  public onCancelStartOverClick() {
    this.displayCancelCollateralPopup = true;
    this.cancelPopUpTitle = 'Cancel and Start Over';
    this.cancelAndStartOverButtonClicked = true;
    this.cancelButtonLabel = 'Yes, Start Over';
    this.cancelUploadCollateralInfoText =
      'By starting over, all data currently displayed will be removed.';
  }
  hideCancelCollateralPopup() {
    this.displayCancelCollateralPopup = false;
    this.uploadNewCollateralLinkClicked = false;
    this.cancelAndStartOverButtonClicked = false;
  }

  public commitMega(): void {
    this.isCommitButtonClicked = true;
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    const commitRequest: any = {};
    commitRequest.poolNumber = this.poolNumber;
    commitRequest.megaType = !isNullOrUndefined(this.calcFeeRes) ? this.calcFeeRes.megaType : null;
    commitRequest.prefixCode = this.calcFeeRes.prefixCode;
    commitRequest.tradeAmount = this.calcFeeRes.tradeAmount;
    commitRequest.tradeSettlementDate = this.calcFeeRes.tradeSettlementDate;
    commitRequest.sfFixedCoupon = this.calcFeeRes.sfFixedCoupon;
    commitRequest.sfARMSubType = this.calcFeeRes.sfARMSubType;
    commitRequest.mfHighCoupon = this.calcFeeRes.mfHighCoupon;
    commitRequest.mfLowCoupon = this.calcFeeRes.mfLowCoupon;
    commitRequest.feeTicks = this.calcFeeRes.feeTicks;
    commitRequest.feeDollars = this.calcFeeRes.feeDollars;
    commitRequest.fannieIssuedAmt = this.calcFeeRes.fannieIssuedAmt;
    commitRequest.freddieIssuedAmt = this.calcFeeRes.freddieIssuedAmt;
    commitRequest.hasFreddieCollateral = this.calcFeeRes.hasFreddieCollateral;
    commitRequest.commitConfirmed = true;
    commitRequest.isCollateralAnalyzer = true;

    commitRequest.tradeRevisionNumber = this.revisionNumber;
    commitRequest.packageRevisionNumber = this.calcFeeRes.packageRevisionNumber;

    this.megaService.commit(commitRequest).subscribe({
      next: (data) => this.handleCommitServiceResponseSuccess(data),
      error: (error) => this.handleCommitServiceResponseFailed(error),
    });
  }

  private handleCommitServiceResponseSuccess(commitResponse) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.passDataService.setCommitMegaResponse(commitResponse);
    this.collateralAllocation();
    this.cdr.markForCheck();
    this.router.navigate(['collateral-analyzer/confirmation']);
  }

  private handleCommitServiceResponseFailed(errors: any) {
    this.errorMsg = '';

    if (errors && errors.status === 400) {
      this.showCommitErrorMessage = true;
      this.errorMsg =
        errors && errors.error && errors.error.messages[0] && errors.error.messages[0].errorMessage;
    }

    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public collateralAllocation(): void {
    const collateralAllocationRequest: any = {};
    const collats: any[] = [];
    const collateralReq: any = [];

    const req: any = new Object();

    collateralAllocationRequest.issueAmount = null;
    collateralAllocationRequest.tradeSettlementDate = null;
    collateralAllocationRequest.feeDollars = null;
    collateralAllocationRequest.allocationStatus = null;
    collateralAllocationRequest.failureReason = null;
    collateralAllocationRequest.maturityDate = null;
    collateralAllocationRequest.wacRate = null;
    collateralAllocationRequest.accntName = null;
    collateralAllocationRequest.telegraphic = null;
    collateralAllocationRequest.subAccnt = null;
    collateralAllocationRequest.abaNumber = null;
    collateralAllocationRequest.freeText = null;

    collateralAllocationRequest.tradeId = null;
    collateralAllocationRequest.trustId =
      this.passDataService.getCommitMegaResponse() &&
      this.passDataService.getCommitMegaResponse().poolNumber;

    this.passDataService.getCollateralCUSIPList().forEach(function (item) {
      const tempCollats: any = {};
      tempCollats.poolId = null;
      tempCollats.cusipId = item.cusip;
      tempCollats.parAmount = item.upbAmount;
      collats.push(tempCollats);
    });

    collateralAllocationRequest.collats = collats;
    collateralReq.push(collateralAllocationRequest);
    req.trades = collateralReq;
    req.userSupplementId = this.userService && this.userService.getUserSupplementId();

    this.passDataService.setCollateralAnalyzerDownloadExcelRequest(req);

    this.megaService.collateralAnalyzerAllocateCollateral(req).subscribe({
      next: (data) => this.handlecollateralAllocationServiceResponseSuccess(data),
      error: (error) => this.handlecollateralAllocationServiceResponseFailed(error),
    });
  }

  private handlecollateralAllocationServiceResponseSuccess(_collateralAllocationRes) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  private handlecollateralAllocationServiceResponseFailed(_errors: any) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public onGoBackAndEdit() {
    this.goBack();
  }

  public closeCommitErrorBanner(): void {
    this.showCommitErrorMessage = false;
  }

  public needsDisclaimer(calcfeeResponse: any): boolean {
    if (
      calcfeeResponse.hasFreddieCollateral &&
      this.feeDisclaimerService.needsDisclaimer(calcfeeResponse.prefixCode)
    ) {
      this.disclaimerMessage = StringConstants.freddieComputedFeeDisclaimer(this.feeTickValue);
      return true;
    }
    return false;
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
